import React from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Timeline, Build, CheckCircle, Warning } from '@mui/icons-material';

export function BuildProcessTab() {
  const buildTools = [
    {
      name: 'Webpack',
      description: 'Module bundler with extensive configuration',
      pros: ['Highly configurable', 'Large ecosystem', 'Production-ready'],
      cons: ['Complex configuration', 'Slow in development', 'Large learning curve']
    },
    {
      name: 'Create React App (CRA)',
      description: 'Zero-configuration setup tool',
      pros: ['Zero config', 'Best practices included', 'Easy to start'],
      cons: ['Limited customization', 'Slower builds', 'Harder to eject']
    },
    {
      name: 'Vite',
      description: 'Fast build tool with native ES modules',
      pros: ['Extremely fast HMR', 'Modern ES modules', 'Simple config'],
      cons: ['Newer ecosystem', 'Some legacy browser issues', 'Learning curve']
    },
    {
      name: 'Next.js',
      description: 'Full-stack React framework with SSR/SSG',
      pros: ['Full-stack capabilities', 'SSR/SSG built-in', 'File-based routing', 'Great performance', 'Turbopack integration'],
      cons: ['Opinionated structure', 'Learning curve', 'Overkill for simple SPAs', 'Framework lock-in']
    },
    {
      name: 'CRACO',
      description: 'CRA override without ejecting',
      pros: ['Customize CRA', 'No ejecting', 'Incremental adoption'],
      cons: ['Additional dependency', 'Limited compared to Webpack', 'Maintenance overhead']
    }
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        React Code → Browser: The Build Journey
      </Typography>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <Timeline sx={{ mr: 1 }} />
            Build Process Steps
          </Typography>
          <Stepper orientation="vertical">
            <Step active>
              <StepLabel>
                <Typography variant="h6">1. Source Code (JSX/TSX)</Typography>
              </StepLabel>
              <StepContent>
                <Typography sx={{ mb: 2 }}>Your React components written in JSX/TSX with modern JavaScript features</Typography>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Common file formats and frameworks:</Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Format/Framework</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>JSX</strong></TableCell>
                        <TableCell>• Familiar HTML-like syntax<br/>• Great developer experience</TableCell>
                        <TableCell>• Requires transpilation<br/>• Not native JavaScript</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>TypeScript (TSX)</strong></TableCell>
                        <TableCell>• Type safety<br/>• Better IDE support<br/>• Catches errors early</TableCell>
                        <TableCell>• Learning curve<br/>• Additional build step<br/>• More configuration</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>JavaScript ES6+</strong></TableCell>
                        <TableCell>• Modern language features<br/>• Better syntax<br/>• Standard compliant</TableCell>
                        <TableCell>• Limited browser support<br/>• Needs transpilation</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </StepContent>
            </Step>
            
            <Step active>
              <StepLabel>
                <Typography variant="h6">2. Transpilation</Typography>
              </StepLabel>
              <StepContent>
                <Typography sx={{ mb: 2 }}>Converts JSX and modern JS to browser-compatible JavaScript</Typography>
                
                <Typography variant="h6" color="primary" sx={{ mt: 2, mb: 1 }}>
                  CRA Solutions
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Tool</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Babel (default in CRA)</strong></TableCell>
                        <TableCell>• Pre-configured in CRA<br/>• Zero setup required<br/>• Battle-tested presets<br/>• Extensive plugin ecosystem</TableCell>
                        <TableCell>• Slow build times<br/>• Limited customization without ejecting<br/>• Hard to update in CRA</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" color="success.main" sx={{ mt: 2, mb: 1 }}>
                  Vite Solutions
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Tool</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>esbuild (default in Vite)</strong></TableCell>
                        <TableCell>• Lightning fast (Go-based)<br/>• Built-in with Vite<br/>• Great development experience<br/>• Simple configuration</TableCell>
                        <TableCell>• Limited plugin ecosystem<br/>• Less configurability than Babel<br/>• Some edge cases with complex transforms</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>SWC (optional in Vite)</strong></TableCell>
                        <TableCell>• Extremely fast (Rust-based)<br/>• Drop-in replacement option<br/>• Growing ecosystem<br/>• Built-in minification</TableCell>
                        <TableCell>• Newer, less mature<br/>• Some compatibility issues<br/>• Fewer plugins than Babel</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" color="secondary.main" sx={{ mt: 2, mb: 1 }}>
                  Next.js Solutions
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Tool</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>SWC (default in Next.js 12+)</strong></TableCell>
                        <TableCell>• Built-in optimization<br/>• Rust-based performance<br/>• TypeScript support<br/>• Zero configuration</TableCell>
                        <TableCell>• Next.js specific<br/>• Limited outside Next.js ecosystem<br/>• Fewer customization options</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" color="warning.main" sx={{ mt: 2, mb: 1 }}>
                  Self Config Webpack
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Tool</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Babel + Webpack</strong></TableCell>
                        <TableCell>• Full control over configuration<br/>• Extensive plugin ecosystem<br/>• Industry standard<br/>• Great browser compatibility</TableCell>
                        <TableCell>• Complex configuration<br/>• Slower than modern alternatives<br/>• Maintenance overhead<br/>• Steep learning curve</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                  Others
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Tool</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>TypeScript Compiler (tsc)</strong></TableCell>
                        <TableCell>• Native TypeScript support<br/>• Type checking included<br/>• Official Microsoft tool<br/>• Great IDE integration</TableCell>
                        <TableCell>• TypeScript only<br/>• Limited JSX transformations<br/>• No polyfills<br/>• Usually combined with other tools</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </StepContent>
            </Step>
            
            <Step active>
              <StepLabel>
                <Typography variant="h6">3. Bundling</Typography>
              </StepLabel>
              <StepContent>
                <Typography sx={{ mb: 2 }}>Combines multiple files into optimized bundles</Typography>
                
                <Typography variant="h6" color="primary" sx={{ mt: 2, mb: 1 }}>
                  CRA Solutions
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Tool</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Webpack (default in CRA)</strong></TableCell>
                        <TableCell>• Pre-configured in CRA<br/>• Zero setup required<br/>• Battle-tested configuration<br/>• Code splitting included</TableCell>
                        <TableCell>• Slow development builds<br/>• Hard to customize without ejecting<br/>• Complex configuration when ejected</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>CRACO (CRA Override)</strong></TableCell>
                        <TableCell>• Customize CRA without ejecting<br/>• Incremental adoption<br/>• Familiar CRA structure<br/>• Community support</TableCell>
                        <TableCell>• Additional dependency<br/>• Limited compared to full Webpack<br/>• Maintenance overhead</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" color="success.main" sx={{ mt: 2, mb: 1 }}>
                  Vite Solutions
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Tool</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Vite Bundler</strong></TableCell>
                        <TableCell>• Extremely fast HMR<br/>• Modern ES modules<br/>• Simple configuration<br/>• Great developer experience</TableCell>
                        <TableCell>• Newer ecosystem<br/>• Some legacy browser issues<br/>• Plugin compatibility varies</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Rollup (production in Vite)</strong></TableCell>
                        <TableCell>• Excellent tree-shaking<br/>• Clean output<br/>• ES modules first<br/>• Used by Vite for production</TableCell>
                        <TableCell>• Development/production difference<br/>• Complex for standalone use<br/>• Limited dev server features</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" color="secondary.main" sx={{ mt: 2, mb: 1 }}>
                  Next.js Solutions
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Tool</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Webpack (default in Next.js)</strong></TableCell>
                        <TableCell>• Full Next.js integration<br/>• SSR/SSG optimized<br/>• File-based routing<br/>• API routes support</TableCell>
                        <TableCell>• Can be slow in large apps<br/>• Next.js specific configuration<br/>• Framework lock-in</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Turbopack (experimental)</strong></TableCell>
                        <TableCell>• Extremely fast<br/>• Rust-based performance<br/>• Incremental compilation<br/>• Next.js optimized</TableCell>
                        <TableCell>• Still experimental<br/>• Next.js only<br/>• Limited outside Next.js ecosystem</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" color="warning.main" sx={{ mt: 2, mb: 1 }}>
                  Self Config Webpack
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Tool</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Custom Webpack Config</strong></TableCell>
                        <TableCell>• Full control over bundling<br/>• Huge plugin ecosystem<br/>• Advanced code splitting<br/>• Production-ready</TableCell>
                        <TableCell>• Complex configuration<br/>• Slow development builds<br/>• Large learning curve<br/>• Maintenance overhead</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                  Others
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Tool</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Parcel</strong></TableCell>
                        <TableCell>• Zero configuration<br/>• Fast out-of-the-box<br/>• Built-in optimizations<br/>• Great for beginners</TableCell>
                        <TableCell>• Less control<br/>• Smaller ecosystem<br/>• Limited customization</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>esbuild</strong></TableCell>
                        <TableCell>• Fastest bundling<br/>• Simple API<br/>• Great for libraries<br/>• Minimal configuration</TableCell>
                        <TableCell>• Limited features<br/>• No HMR<br/>• Basic development server</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Rollup (standalone)</strong></TableCell>
                        <TableCell>• Excellent tree-shaking<br/>• Clean output<br/>• Great for libraries<br/>• ES modules first</TableCell>
                        <TableCell>• Complex for apps<br/>• Limited dev server<br/>• Requires more setup</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </StepContent>
            </Step>
            
            <Step active>
              <StepLabel>
                <Typography variant="h6">4. Optimization</Typography>
              </StepLabel>
              <StepContent>
                <Typography sx={{ mb: 2 }}>Minification, tree-shaking, code splitting for performance</Typography>
                
                <Typography variant="h6" color="primary" sx={{ mt: 2, mb: 1 }}>
                  CRA Solutions
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Tool</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Terser (default in CRA)</strong></TableCell>
                        <TableCell>• Pre-configured minification<br/>• Great compression<br/>• ES6+ support<br/>• Battle-tested</TableCell>
                        <TableCell>• Slow build times<br/>• Memory intensive<br/>• Hard to customize in CRA</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Webpack Optimizations</strong></TableCell>
                        <TableCell>• Code splitting included<br/>• Tree-shaking enabled<br/>• Chunk optimization<br/>• Production optimized</TableCell>
                        <TableCell>• Complex configuration<br/>• Slow optimization process<br/>• Hard to customize without ejecting</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" color="success.main" sx={{ mt: 2, mb: 1 }}>
                  Vite Solutions
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Tool</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>esbuild (default in Vite)</strong></TableCell>
                        <TableCell>• Extremely fast minification<br/>• Great compression ratio<br/>• Simple configuration<br/>• Go-based performance</TableCell>
                        <TableCell>• Fewer optimization options<br/>• Less mature than Terser<br/>• Limited customization</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Rollup Optimizations</strong></TableCell>
                        <TableCell>• Excellent tree-shaking<br/>• Clean output<br/>• ES modules optimized<br/>• Used in Vite production builds</TableCell>
                        <TableCell>• Development/production difference<br/>• Can be overly aggressive<br/>• Side effects handling</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" color="secondary.main" sx={{ mt: 2, mb: 1 }}>
                  Next.js Solutions
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Tool</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>SWC (default in Next.js 12+)</strong></TableCell>
                        <TableCell>• Very fast (Rust-based)<br/>• Good compression<br/>• Built-in optimization<br/>• Active development</TableCell>
                        <TableCell>• Newer tool<br/>• Less ecosystem<br/>• Next.js specific configuration</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Next.js Optimizations</strong></TableCell>
                        <TableCell>• Automatic code splitting<br/>• Image optimization<br/>• Bundle analysis<br/>• Performance insights</TableCell>
                        <TableCell>• Framework specific<br/>• Less control<br/>• Next.js lock-in</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" color="warning.main" sx={{ mt: 2, mb: 1 }}>
                  Self Config Webpack
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Tool</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Custom Webpack Optimizations</strong></TableCell>
                        <TableCell>• Full control over optimization<br/>• Custom plugins<br/>• Advanced code splitting<br/>• Module federation</TableCell>
                        <TableCell>• Complex setup<br/>• Learning curve<br/>• Configuration heavy<br/>• Maintenance overhead</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Terser + Custom Config</strong></TableCell>
                        <TableCell>• Highly configurable<br/>• Industry standard<br/>• Fine-grained control<br/>• Production proven</TableCell>
                        <TableCell>• Slower than alternatives<br/>• Complex configuration<br/>• Memory intensive</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                  Others
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Tool</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Parcel Optimizations</strong></TableCell>
                        <TableCell>• Zero configuration<br/>• Built-in optimizations<br/>• Automatic code splitting<br/>• Multi-core processing</TableCell>
                        <TableCell>• Less control<br/>• Limited customization<br/>• Smaller ecosystem</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </StepContent>
            </Step>
            
            <Step active>
              <StepLabel>
                <Typography variant="h6">5. Browser-Ready Code</Typography>
              </StepLabel>
              <StepContent>
                <Typography sx={{ mb: 2 }}>Final output formats and deployment options</Typography>
                
                <Typography variant="h6" color="primary" sx={{ mt: 2, mb: 1 }}>
                  CRA Solutions
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Output Format</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Static Files (SPA only)</strong></TableCell>
                        <TableCell>• Simple deployment<br/>• CDN-friendly<br/>• Pre-configured build<br/>• Easy CI/CD setup</TableCell>
                        <TableCell>• SEO challenges<br/>• Large initial bundle<br/>• Client-side routing issues<br/>• No SSR support</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" color="success.main" sx={{ mt: 2, mb: 1 }}>
                  Vite Solutions
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Output Format</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Optimized Static Files</strong></TableCell>
                        <TableCell>• Fast builds<br/>• Small bundles<br/>• Modern ES modules<br/>• Tree-shaking optimized</TableCell>
                        <TableCell>• SPA only (without SSR frameworks)<br/>• SEO challenges for SPA<br/>• Legacy browser compatibility</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>PWA Support</strong></TableCell>
                        <TableCell>• Built-in PWA plugin<br/>• Service worker generation<br/>• Offline capability<br/>• App-like experience</TableCell>
                        <TableCell>• Additional configuration<br/>• Browser limitations<br/>• Extra maintenance</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" color="secondary.main" sx={{ mt: 2, mb: 1 }}>
                  Next.js Solutions
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Output Format</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Static Site Generated (SSG)</strong></TableCell>
                        <TableCell>• Best performance<br/>• SEO optimized<br/>• CDN deployment<br/>• Pre-rendered pages</TableCell>
                        <TableCell>• Build time increases<br/>• Dynamic content limitations<br/>• Rebuild for updates</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Server-Side Rendered (SSR)</strong></TableCell>
                        <TableCell>• Dynamic content<br/>• Better SEO<br/>• Faster initial paint<br/>• Social media sharing</TableCell>
                        <TableCell>• Server requirements<br/>• Higher costs<br/>• Complex deployment<br/>• Development complexity</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Hybrid (SSG + SSR)</strong></TableCell>
                        <TableCell>• Flexible rendering<br/>• Page-level optimization<br/>• Best of both worlds<br/>• Incremental Static Regeneration</TableCell>
                        <TableCell>• Complex configuration<br/>• Learning curve<br/>• Deployment complexity</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" color="warning.main" sx={{ mt: 2, mb: 1 }}>
                  Self Config Webpack
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Output Format</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Custom Build Output</strong></TableCell>
                        <TableCell>• Full control<br/>• Multiple entry points<br/>• Advanced code splitting<br/>• Module federation</TableCell>
                        <TableCell>• Complex configuration<br/>• Maintenance overhead<br/>• Learning curve<br/>• Build performance</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                  Others
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Output Format</strong></TableCell>
                        <TableCell><strong>Pros</strong></TableCell>
                        <TableCell><strong>Cons</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Gatsby (SSG)</strong></TableCell>
                        <TableCell>• Excellent for content sites<br/>• Plugin ecosystem<br/>• GraphQL integration<br/>• Performance optimized</TableCell>
                        <TableCell>• Complex for simple sites<br/>• Build time with large sites<br/>• Learning curve</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Remix (SSR)</strong></TableCell>
                        <TableCell>• Web standards focused<br/>• Progressive enhancement<br/>• Great UX patterns<br/>• Excellent data loading</TableCell>
                        <TableCell>• Newer framework<br/>• Different mental model<br/>• Smaller ecosystem</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </StepContent>
            </Step>
          </Stepper>
        </CardContent>
      </Card>

      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Build Tool Comparison
      </Typography>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {buildTools.map((tool) => (
          <Box key={tool.name} sx={{ flex: '1 1 300px', minWidth: '300px' }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Build sx={{ mr: 1 }} />
                  {tool.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {tool.description}
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="success.main">Pros:</Typography>
                  <List dense>
                    {tool.pros.map((pro, index) => (
                      <ListItem key={index} sx={{ py: 0 }}>
                        <ListItemIcon sx={{ minWidth: 20 }}>
                          <CheckCircle color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={pro} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="warning.main">Cons:</Typography>
                  <List dense>
                    {tool.cons.map((con, index) => (
                      <ListItem key={index} sx={{ py: 0 }}>
                        <ListItemIcon sx={{ minWidth: 20 }}>
                          <Warning color="warning" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={con} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
